/**
 * WebAccountPageService - Web Platform Account Page Service Implementation
 *
 * Implements IAccountPageService for web platform using existing services
 * Integrates with authStore, navigationService, and other web-specific APIs
 */

import type {
  IAccountPageService,
  UserAccountSummary,
  INavigationService
} from '../../contracts'
import type { AccountPageTile } from '@restaurant/core'
import { useAuthStore, useRestaurantStore, useDeliveryStore, getSupabaseClient } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


// Mock account page tile data - matches DEFAULT_ACCOUNT_TILES structure
const ACCOUNT_NAVIGATION_TILES: AccountPageTile[] = [
  {
    id: 'manage-account',
    pageTitle: 'Manage Account',
    iconName: 'accountSettings',
    accessibilityLabel: 'Navigate to account management',
  },
  {
    id: 'notifications',
    pageTitle: 'Notifications',
    iconName: 'bell',
    accessibilityLabel: 'Navigate to notification settings',
  },
  {
    id: 'favorites-list',
    pageTitle: 'Favorites List',
    iconName: 'favoritesList',
    accessibilityLabel: 'Navigate to favorite items',
  },
  {
    id: 'order-history',
    pageTitle: 'Order History',
    iconName: 'history',
    accessibilityLabel: 'Navigate to order history',
  },
  {
    id: 'help-feedback',
    pageTitle: 'Help and Feedback',
    iconName: 'help',
    accessibilityLabel: 'Navigate to help and feedback',
  },
]

// Navigation route mapping for tiles
const TILE_ROUTE_MAP: Record<string, string> = {
  'manage-account': '/account/manage',
  'notifications': '/account/notifications',
  'favorites-list': '/favorites',
  'order-history': '/orders',
  'addresses': '/account/addresses',
  'help-feedback': '/help',
}

export class WebAccountPageService implements IAccountPageService {
  constructor(
    private navigationService: INavigationService
  ) {}

  /**
   * Check if user has any favorite items
   * Uses an efficient count query (head: true) to avoid fetching data
   */
  private async hasFavorites(userId: string, restaurantStoreId: string): Promise<boolean> {
    try {
      const supabase = getSupabaseClient()
      const { count, error } = await supabase
        .from('user_favorites')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('restaurant_store_id', restaurantStoreId)

      return !error && (count ?? 0) > 0
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to check favorites', { domain: 'favorites', error: String(error) })
      return false
    }
  }

  /**
   * Get user account summary from auth store
   */
  async getUserAccountSummary(): Promise<UserAccountSummary | null> {
    try {
      const authState = useAuthStore.getState()

      // Check if user is authenticated
      if (!authState.isAuthenticated || !authState.user) {
        return null
      }

      const user = authState.user

      // Build user summary from auth state
      const userSummary: UserAccountSummary = {
        displayName: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : user.email || 'User',
        email: user.email || '',
        phone: user.phoneNumber,
        isVerified: user.emailVerified || false,
        rewardPoints: user.loyaltyPoints || 0,
        tier: 'Bronze',
      }

      return userSummary
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get user account summary', { domain: 'auth', error: String(error) })
      return null
    }
  }

  /**
   * Get available navigation tiles for the current user
   * Dynamically filters tiles based on user state (e.g., favorites)
   */
  async getAccountNavigationTiles() {
    try {
      const authState = useAuthStore.getState()
      const restaurantState = useRestaurantStore.getState()

      let tiles = [...ACCOUNT_NAVIGATION_TILES]

      // Only show Favorites List tile if user has favorites
      if (authState.isAuthenticated && authState.user && restaurantState.currentRestaurant?.id) {
        const userHasFavorites = await this.hasFavorites(
          authState.user.id,
          restaurantState.currentRestaurant.id
        )
        if (!userHasFavorites) {
          tiles = tiles.filter(tile => tile.id !== 'favorites-list')
        }
      } else {
        // If not authenticated or no restaurant, hide favorites
        tiles = tiles.filter(tile => tile.id !== 'favorites-list')
      }

      // Only show Addresses tile if user has saved addresses
      // Check both in-memory store and persisted localStorage (store may not have hydrated yet)
      const deliveryState = useDeliveryStore.getState()
      let hasSavedAddresses = deliveryState.savedAddresses.length > 0
      if (!hasSavedAddresses) {
        try {
          const persisted = localStorage.getItem('delivery-address-storage')
          if (persisted) {
            const parsed = JSON.parse(persisted)
            hasSavedAddresses = (parsed?.state?.savedAddresses?.length ?? 0) > 0
          }
        } catch { /* ignore parse errors */ }
      }
      if (!hasSavedAddresses) {
        tiles = tiles.filter(tile => tile.id !== 'addresses')
      }

      return tiles
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get account navigation tiles', { domain: 'auth', error: String(error) })
      return []
    }
  }

  /**
   * Handle navigation tile click
   */
  async handleTileNavigation(tileId: string): Promise<void> {
    try {
      const route = TILE_ROUTE_MAP[tileId]

      if (route) {
        this.navigationService.navigate(route)
      } else {
        const logger = safeGetLogger()
        logger?.warn('No route mapped for account tile', { tileId })
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error(`Failed to navigate to tile ${tileId}`, { domain: 'auth', error: String(error) })
      throw new Error(`Navigation failed for ${tileId}`)
    }
  }

  /**
   * Check if user has access to specific tile
   */
  async hasAccessToTile(tileId: string): Promise<boolean> {
    try {
      const authState = useAuthStore.getState()

      // Basic access control - user must be authenticated
      if (!authState.isAuthenticated) {
        return false
      }

      // Could add more sophisticated access control here based on user tier, permissions, etc.
      // For now, authenticated users have access to all tiles
      return true
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error(`Failed to check access for tile ${tileId}`, { domain: 'auth', error: String(error) })
      return false
    }
  }

  /**
   * Get tile with updated badges/notifications
   */
  async getTileWithNotifications(tileId: string) {
    try {
      const baseTile = ACCOUNT_NAVIGATION_TILES.find(tile => tile.id === tileId)

      if (!baseTile) {
        return null
      }

      // Could add notification badges here based on tile type
      // For example, unread notifications count, pending orders, etc.

      return { ...baseTile }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error(`Failed to get tile with notifications for ${tileId}`, { domain: 'auth', error: String(error) })
      return null
    }
  }
}
